import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Editor } from "@tinymce/tinymce-react";
import "tinymce/tinymce";
import "tinymce/icons/default";
import "tinymce/themes/silver";
import "tinymce/models/dom";
import "tinymce/plugins/link";
import "tinymce/plugins/lists";
import "tinymce/plugins/code";
import "tinymce/plugins/table";
import "tinymce/plugins/autolink";
import "tinymce/skins/ui/oxide/skin.min.css";
import "tinymce/skins/ui/oxide/content.min.css";
import "tinymce/skins/content/default/content.min.css";

declare global {
  interface Window {
    google?: {
      accounts?: {
        id?: {
          initialize: (options: { client_id: string; callback: (r: { credential?: string }) => void }) => void;
          renderButton: (parent: HTMLElement, options: Record<string, unknown>) => void;
        };
      };
    };
  }
}

type EventSummary = {
  id: string;
  slug: string;
  title: string;
  titleZh: string;
  venue: string;
  dateLabel: string;
  pendingReviewCount: number;
  approvedCount: number;
  rejectedCount: number;
  checkedInCount: number;
  autoApprove: boolean;
  isActive: boolean;
};

type EmailLog = {
  id: string;
  createdAt: string;
  registrationId: string | null;
  eventSlug: string | null;
  recipientEmail: string;
  emailType: "pending_review" | "approved_ticket" | "resend_ticket";
  status: "success" | "failure";
  errorMessage: string | null;
  metadata: Record<string, unknown> | null;
};

type EventRegistrationRow = {
  id: string;
  createdAt: string;
  updatedAt: string;
  eventSlug: string;
  eventTitle: string;
  eventTitleZh: string;
  ticketTypeCode: string;
  ticketTypeName: string;
  ticketTypeNameZh: string;
  ticketPriceHkd: number | null;
  fullName: string;
  displayName: string | null;
  phone: string;
  email: string;
  receiptFileName: string | null;
  receiptMimeType: string | null;
  receiptUploadedAt: string;
  status: "pending_review" | "approved" | "rejected";
  statusReason: string | null;
  adminNotes: string | null;
  eTicketCode: string | null;
  approvedAt: string | null;
  rejectedAt: string | null;
  checkedInAt: string | null;
  ticketEmailSentAt: string | null;
  ticketUrl: string | null;
  receiptUrl: string | null;
  discountCode: string | null;
  paymentMethod: string | null;
  customValues: Record<string, string> | null;
};

type BaseColumnKey =
  | "created"
  | "event"
  | "attendee"
  | "contact"
  | "ticket"
  | "status"
  | "discCode"
  | "payment"
  | "custom"
  | "eTicket"
  | "checkin"
  | "receipt"
  | "remarks"
  | "actions";

type ColumnDef = {
  key: string;
  label: string;
  baseKey?: BaseColumnKey;
  isCustom?: boolean;
};

const tokenStorageKey = "aurays_admin_token";

const statusBadgeClass: Record<EventRegistrationRow["status"], string> = {
  pending_review: "bg-amber-50 text-amber-700",
  approved: "bg-emerald-50 text-emerald-700",
  rejected: "bg-red-50 text-red-700",
};

const escapeHtml = (value: unknown) =>
  String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const applyTemplateVars = (html: string, vars: Record<string, string>) => {
  let out = html;
  for (const [key, value] of Object.entries(vars)) {
    const re = new RegExp(`\\{\\{\\s*${escapeRegExp(key)}\\s*\\}\\}`, "g");
    out = out.replace(re, value);
  }
  return out;
};

export default function RegistrationOpsPage() {
  const [token, setToken] = useState("");
  const [authed, setAuthed] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState("");
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [noteInputValue, setNoteInputValue] = useState("");
  const [editingRegistration, setEditingRegistration] = useState<EventRegistrationRow | null>(null);
  const [editFormData, setEditFormData] = useState<{
    fullName: string;
    displayName: string;
    phone: string;
    email: string;
    ticketTypeCode: string;
    discountCode: string;
    customValues: Record<string, string>;
  } | null>(null);
  const [editEventCustomFields, setEditEventCustomFields] = useState<
    {
      id: string;
      fieldKey: string;
      labelEn: string;
      labelZh: string;
      fieldType: string;
      options: Array<{ en: string; zh: string }>;
      required: boolean;
      sortOrder: number;
      isActive: boolean;
      parentFieldKey: string | null;
      parentValue: string | null;
    }[]
  >([]);
  const googleButtonRef = useRef<HTMLDivElement | null>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [eventSummaries, setEventSummaries] = useState<EventSummary[]>([]);
  const [eventRegistrations, setEventRegistrations] = useState<EventRegistrationRow[]>([]);
  const [selectedEventSlug, setSelectedEventSlug] = useState("");
  const [eventStatusFilter, setEventStatusFilter] = useState("");
  const [eventSearch, setEventSearch] = useState("");
  const [receiptModal, setReceiptModal] = useState<EventRegistrationRow | null>(null);
  const [undoCheckInModal, setUndoCheckInModal] = useState<EventRegistrationRow | null>(null);
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const [selectedRegistrationIds, setSelectedRegistrationIds] = useState<string[]>([]);
  const selectedRegistrationIdSet = useMemo(() => new Set(selectedRegistrationIds), [selectedRegistrationIds]);
  const [customEmailModalOpen, setCustomEmailModalOpen] = useState(false);
  const [customEmailSubject, setCustomEmailSubject] = useState("");
  const [customEmailBody, setCustomEmailBody] = useState("");
  const [customEmailTab, setCustomEmailTab] = useState<"edit" | "preview">("edit");
  const [customEmailSending, setCustomEmailSending] = useState(false);
  const customEmailEditorRef = useRef<any>(null);
  const [visibleColumnKeys, setVisibleColumnKeys] = useState<string[]>([
    "created",
    "event",
    "attendee",
    "contact",
    "ticket",
    "status",
    "discCode",
    "payment",
    "custom",
    "eTicket",
    "checkin",
    "receipt",
    "remarks",
    "actions",
  ]);

  // Email logs state
  const [activeTab, setActiveTab] = useState<"registrations" | "email-logs">("registrations");
  const [emailLogs, setEmailLogs] = useState<EmailLog[]>([]);
  const [emailLogsLoading, setEmailLogsLoading] = useState(false);
  const [emailLogEventSlug, setEmailLogEventSlug] = useState("");
  const [emailLogStatus, setEmailLogStatus] = useState("");
  const [emailLogRecipient, setEmailLogRecipient] = useState("");
  const [emailLogType, setEmailLogType] = useState("");
  const [selectedEmailLog, setSelectedEmailLog] = useState<EmailLog | null>(null);
  const [batchResendLoading, setBatchResendLoading] = useState(false);
  const [batchUndoCheckInLoading, setBatchUndoCheckInLoading] = useState(false);

  // --- Column resize ---
  const [colWidths, setColWidths] = useState<Record<string, number>>({
    created: 160,
    event: 140,
    attendee: 140,
    contact: 170,
    ticket: 120,
    status: 120,
    discCode: 100,
    payment: 140,
    custom: 140,
    eTicket: 120,
    checkin: 100,
    receipt: 100,
    remarks: 200,
    actions: 130,
  });
  const resizeRef = useRef<{ colKey: string; startX: number; startWidth: number } | null>(null);

  const selectedEventCustomFieldKeys = useMemo(() => {
    if (!selectedEventSlug) return [] as string[];
    const keys = new Set<string>();
    for (const row of eventRegistrations) {
      if (row.eventSlug !== selectedEventSlug || !row.customValues) continue;
      Object.keys(row.customValues).forEach((key) => keys.add(key));
    }
    return Array.from(keys);
  }, [eventRegistrations, selectedEventSlug]);

  useEffect(() => {
    setSelectedRegistrationIds((prev) => {
      if (prev.length === 0) return prev;
      const allowed = new Set(eventRegistrations.map((r) => r.id));
      return prev.filter((id) => allowed.has(id));
    });
  }, [eventRegistrations]);

  const allColumns = useMemo<ColumnDef[]>(() => {
    const baseColumns: ColumnDef[] = [
      { key: "created", label: "Created", baseKey: "created" },
      { key: "event", label: "Event", baseKey: "event" },
      { key: "attendee", label: "Attendee", baseKey: "attendee" },
      { key: "contact", label: "Contact", baseKey: "contact" },
      { key: "ticket", label: "Ticket", baseKey: "ticket" },
      { key: "status", label: "Status", baseKey: "status" },
      { key: "discCode", label: "Disc. Code", baseKey: "discCode" },
      { key: "payment", label: "Payment", baseKey: "payment" },
      ...(selectedEventSlug
        ? selectedEventCustomFieldKeys.map((fieldKey) => ({
            key: `custom:${fieldKey}`,
            label: fieldKey,
            isCustom: true,
          }))
        : [{ key: "custom", label: "Custom", baseKey: "custom" as BaseColumnKey }]),
      { key: "eTicket", label: "E-Ticket", baseKey: "eTicket" },
      { key: "checkin", label: "Check-in", baseKey: "checkin" },
      { key: "receipt", label: "Receipt", baseKey: "receipt" },
      { key: "remarks", label: "Remarks", baseKey: "remarks" },
      { key: "actions", label: "Actions", baseKey: "actions" },
    ];
    return baseColumns;
  }, [selectedEventCustomFieldKeys, selectedEventSlug]);

  const activeColumns = useMemo(
    () => allColumns.filter((column) => visibleColumnKeys.includes(column.key)),
    [allColumns, visibleColumnKeys],
  );

  const selectAllRef = useRef<HTMLInputElement | null>(null);
  const allVisibleSelected = useMemo(
    () => eventRegistrations.length > 0 && eventRegistrations.every((r) => selectedRegistrationIdSet.has(r.id)),
    [eventRegistrations, selectedRegistrationIdSet],
  );
  const someVisibleSelected = useMemo(
    () => eventRegistrations.some((r) => selectedRegistrationIdSet.has(r.id)),
    [eventRegistrations, selectedRegistrationIdSet],
  );

  useEffect(() => {
    if (!selectAllRef.current) return;
    selectAllRef.current.indeterminate = someVisibleSelected && !allVisibleSelected;
  }, [allVisibleSelected, someVisibleSelected]);

  const toggleSelectedRegistration = useCallback((id: string) => {
    setSelectedRegistrationIds((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  }, []);

  const toggleSelectAllVisible = useCallback(
    (checked: boolean) => {
      setSelectedRegistrationIds(checked ? eventRegistrations.map((r) => r.id) : []);
    },
    [eventRegistrations],
  );

  const insertCustomEmailToken = useCallback((token: string) => {
    const editor = customEmailEditorRef.current;
    if (editor?.insertContent) {
      editor.insertContent(token);
      return;
    }
    setCustomEmailBody((prev) => `${prev}${token}`);
  }, []);

  const previewRegistration = useMemo(() => {
    const firstSelectedId = selectedRegistrationIds[0];
    if (!firstSelectedId) return null;
    return eventRegistrations.find((r) => r.id === firstSelectedId) || null;
  }, [eventRegistrations, selectedRegistrationIds]);

  const customEmailBodyPreview = useMemo(() => {
    if (!previewRegistration) return customEmailBody;
    return applyTemplateVars(customEmailBody, {
      fullName: escapeHtml(previewRegistration.fullName),
      displayName: escapeHtml(previewRegistration.displayName || previewRegistration.fullName),
      eventSlug: escapeHtml(previewRegistration.eventSlug),
      eTicketCode: escapeHtml(previewRegistration.eTicketCode || ""),
      ticketUrl: escapeHtml(previewRegistration.ticketUrl || ""),
    });
  }, [customEmailBody, previewRegistration]);

  useEffect(() => {
    setVisibleColumnKeys((prev) => {
      const next = allColumns.map((column) => column.key);
      const preserved = prev.filter((key) => next.includes(key));
      const missing = next.filter((key) => !preserved.includes(key));
      return [...preserved, ...missing];
    });
    setColWidths((prev) => {
      const next = { ...prev };
      for (const column of allColumns) {
        if (next[column.key] === undefined) {
          next[column.key] = column.isCustom ? 140 : 120;
        }
      }
      return next;
    });
  }, [allColumns]);

  const toggleColumnVisibility = useCallback((key: string) => {
    setVisibleColumnKeys((prev) => (prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]));
  }, []);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!resizeRef.current) return;
      const { colKey, startX, startWidth } = resizeRef.current;
      const delta = e.clientX - startX;
      const newWidth = Math.max(60, startWidth + delta);
      setColWidths((prev) => ({ ...prev, [colKey]: newWidth }));
    };
    const onUp = () => {
      resizeRef.current = null;
      document.body.classList.remove("select-none", "cursor-col-resize");
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, []);

  const handleResizeStart = (colKey: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    resizeRef.current = { colKey, startX: e.clientX, startWidth: colWidths[colKey] || 120 };
    document.body.classList.add("select-none", "cursor-col-resize");
  };

  const authHeaders = useMemo(() => {
    const t = token.trim();
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, [token]);

  // Initialize edit form when editingRegistration changes and fetch custom fields
  useEffect(() => {
    if (editingRegistration) {
      // Find matching event from summaries to get event ID
      const event = eventSummaries.find((e) => e.slug === editingRegistration.eventSlug);
      // Initialize edit form
      const registrationCustomValues = editingRegistration.customValues || {};

      setEditFormData({
        fullName: editingRegistration.fullName,
        displayName: editingRegistration.displayName || "",
        phone: editingRegistration.phone,
        email: editingRegistration.email,
        ticketTypeCode: editingRegistration.ticketTypeCode,
        discountCode: editingRegistration.discountCode || "",
        customValues: { ...registrationCustomValues },
      });

      // Fetch custom fields for event if we have event ID
      if (event?.id) {
        const fetchCustomFields = async () => {
          try {
            const res = await fetch(`/api/admin/events/${encodeURIComponent(event.id)}/custom-fields`, {
              headers: authHeaders,
            });
            const data = (await res.json().catch(() => null)) as { ok?: boolean; fields?: any[] } | null;
            if (data?.ok && data.fields) {
              setEditEventCustomFields(data.fields);
              // Also update customValues to include all field keys (with empty string if missing)
              setEditFormData((prev) => {
                if (!prev) return prev;
                const allCustomValues = { ...prev.customValues };
                for (const field of data.fields) {
                  if (!(field.fieldKey in allCustomValues)) {
                    allCustomValues[field.fieldKey] = "";
                  }
                }
                return { ...prev, customValues: allCustomValues };
              });
            } else {
              setEditEventCustomFields([]);
            }
          } catch {
            setEditEventCustomFields([]);
          }
        };
        fetchCustomFields();
      } else {
        setEditEventCustomFields([]);
      }
    } else {
      setEditEventCustomFields([]);
    }
  }, [editingRegistration, eventSummaries, authHeaders]);

  const setAdminToken = (nextToken: string) => {
    const t = nextToken.trim();
    if (t) {
      sessionStorage.setItem(tokenStorageKey, t);
      setToken(t);
    }
  };

  useEffect(() => {
    const savedToken = sessionStorage.getItem(tokenStorageKey);
    if (savedToken) {
      setToken(savedToken);
      setAuthed(true);
    }
  }, []);

  const loginWithGoogleIdToken = useCallback(async (idToken: string) => {
    setError("");
    setLoading(true);
    try {
      const res = await fetch("/api/admin/login/google", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idToken }),
      });
      const data = (await res.json().catch(() => null)) as {
        ok?: boolean;
        token?: string | null;
        error?: string;
      } | null;
      if (!res.ok || !data?.ok || typeof data.token !== "string" || !data.token.trim()) {
        setAuthed(false);
        setError(data?.error || "Google login failed.");
        return;
      }
      setAdminToken(data.token);
      setAuthed(true);
    } catch {
      setAuthed(false);
      setError("Google login failed.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authed) return;
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;
    const container = googleButtonRef.current;
    if (!clientId || !container) return;

    const render = () => {
      const google = window.google?.accounts?.id;
      if (!google?.initialize || !google?.renderButton) return;
      container.innerHTML = "";
      google.initialize({
        client_id: clientId,
        callback: (response) => {
          const credential = typeof response?.credential === "string" ? response.credential : "";
          if (credential) void loginWithGoogleIdToken(credential);
        },
      });
      google.renderButton(container, {
        type: "standard",
        theme: "outline",
        size: "large",
        width: 320,
        text: "signin_with",
        shape: "pill",
      });
    };

    if (window.google?.accounts?.id) {
      render();
      return;
    }

    const existing = document.getElementById("google-gsi");
    if (existing) {
      const interval = window.setInterval(() => {
        if (window.google?.accounts?.id) {
          window.clearInterval(interval);
          render();
        }
      }, 50);
      return () => window.clearInterval(interval);
    }

    const script = document.createElement("script");
    script.id = "google-gsi";
    script.async = true;
    script.defer = true;
    script.src = "https://accounts.google.com/gsi/client";
    script.onload = () => render();
    document.head.appendChild(script);
  }, [authed, loginWithGoogleIdToken]);

  const loadEventSummaries = useCallback(async () => {
    try {
      const res = await fetch("/api/admin/events", { headers: authHeaders });
      if (!res.ok) throw new Error("Request failed");
      const data = (await res.json()) as { ok?: boolean; rows?: EventSummary[] };
      if (!data.ok || !Array.isArray(data.rows)) throw new Error("Request failed");
      setEventSummaries(data.rows);
      if (!selectedEventSlug && data.rows[0]?.slug) {
        setSelectedEventSlug(data.rows[0].slug);
      }
    } catch {
      setError("Failed to load event summaries.");
    }
  }, [authHeaders, selectedEventSlug]);

  const loadEventRegistrations = useCallback(async () => {
    setError("");
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedEventSlug) params.set("eventSlug", selectedEventSlug);
      if (eventStatusFilter) params.set("status", eventStatusFilter);
      if (eventSearch.trim()) params.set("search", eventSearch.trim());
      const res = await fetch(`/api/admin/event-registrations?${params.toString()}`, { headers: authHeaders });
      if (!res.ok) throw new Error("Request failed");
      const data = (await res.json()) as { ok?: boolean; rows?: EventRegistrationRow[] };
      if (!data.ok || !Array.isArray(data.rows)) throw new Error("Request failed");
      setEventRegistrations(data.rows);
    } catch {
      setError("Failed to load event registrations.");
    } finally {
      setLoading(false);
    }
  }, [authHeaders, eventSearch, eventStatusFilter, selectedEventSlug]);

  const loadEmailLogs = useCallback(async () => {
    setEmailLogsLoading(true);
    try {
      const params = new URLSearchParams();
      if (emailLogEventSlug) params.set("eventSlug", emailLogEventSlug);
      if (emailLogStatus) params.set("status", emailLogStatus);
      if (emailLogRecipient) params.set("recipientEmail", emailLogRecipient);
      if (emailLogType) params.set("emailType", emailLogType);
      const res = await fetch(`/api/admin/email-logs?${params.toString()}`, { headers: authHeaders });
      if (!res.ok) throw new Error("Request failed");
      const data = (await res.json()) as { ok?: boolean; logs?: EmailLog[] };
      if (!data.ok || !Array.isArray(data.logs)) throw new Error("Request failed");
      setEmailLogs(data.logs);
    } catch {
      setError("Failed to load email logs.");
    } finally {
      setEmailLogsLoading(false);
    }
  }, [authHeaders, emailLogEventSlug, emailLogStatus, emailLogRecipient, emailLogType]);

  const updateEventRegistration = useCallback(
    async (id: string, action: "approve" | "reject" | "check-in", payload?: Record<string, string>) => {
      setError("");
      setActionLoadingId(id);
      try {
        const body = payload ? JSON.stringify(payload) : undefined;
        const headers = { ...authHeaders };
        if (body) {
          headers["Content-Type"] = "application/json";
        }
        const res = await fetch(`/api/admin/event-registrations/${encodeURIComponent(id)}/${action}`, {
          method: "POST",
          headers,
          body,
        });
        const data = (await res.json().catch(() => null)) as { ok?: boolean; error?: string } | null;
        if (!res.ok || !data?.ok) {
          throw new Error(data?.error || "action_failed");
        }
        await loadEventSummaries();
        await loadEventRegistrations();
      } catch {
        const fallback =
          action === "approve"
            ? "Failed to approve registration."
            : action === "reject"
              ? "Failed to reject registration."
              : "Failed to check in attendee.";
        setError(fallback);
      } finally {
        setActionLoadingId("");
      }
    },
    [authHeaders, loadEventRegistrations, loadEventSummaries],
  );

  const updateAdminNotes = useCallback(
    async (id: string, notes: string) => {
      setError("");
      setActionLoadingId(id);
      try {
        const res = await fetch(`/api/admin/event-registrations/${encodeURIComponent(id)}/notes`, {
          method: "POST",
          headers: { ...authHeaders, "Content-Type": "application/json" },
          body: JSON.stringify({ adminNotes: notes }),
        });
        const data = (await res.json().catch(() => null)) as { ok?: boolean; error?: string } | null;
        if (!res.ok || !data?.ok) {
          throw new Error(data?.error || "update_failed");
        }
        await loadEventSummaries();
        await loadEventRegistrations();
      } catch {
        setError("Failed to save notes.");
      } finally {
        setActionLoadingId("");
        setEditingNoteId(null);
        setNoteInputValue("");
      }
    },
    [authHeaders, loadEventRegistrations, loadEventSummaries],
  );

  const resendTicketEmail = useCallback(
    async (id: string) => {
      setError("");
      setActionLoadingId(id);
      try {
        const res = await fetch(`/api/admin/event-registrations/${encodeURIComponent(id)}/resend-ticket`, {
          method: "POST",
          headers: authHeaders,
        });
        const data = (await res.json().catch(() => null)) as { ok?: boolean; error?: string } | null;
        if (!res.ok || !data?.ok) {
          throw new Error(data?.error || "resend_failed");
        }
        await loadEventSummaries();
        await loadEventRegistrations();
      } catch {
        setError("Failed to resend ticket email.");
      } finally {
        setActionLoadingId("");
      }
    },
    [authHeaders, loadEventRegistrations, loadEventSummaries],
  );

  const handleBatchResend = useCallback(async () => {
    if (!selectedEventSlug) return;
    const confirmed = window.confirm(
      "Are you sure you want to resend ticket emails to ALL approved participants for this event?\n\n確定要重新發送門票郵件給此活動的所有已確認參加者嗎？",
    );
    if (!confirmed) return;
    setError("");
    setBatchResendLoading(true);
    try {
      const res = await fetch(`/api/admin/events/${encodeURIComponent(selectedEventSlug)}/batch-resend-tickets`, {
        method: "POST",
        headers: authHeaders,
      });
      const data = (await res.json().catch(() => null)) as {
        ok?: boolean;
        error?: string;
        total?: number;
        success?: number;
        failed?: number;
      } | null;
      if (!res.ok || !data?.ok) {
        throw new Error(data?.error || "batch_resend_failed");
      }
      alert(`Batch resend complete! Success: ${data?.success}/${data?.total}, Failed: ${data?.failed}/${data?.total}`);
      await loadEventSummaries();
      await loadEventRegistrations();
    } catch {
      setError("Failed to batch resend tickets.");
    } finally {
      setBatchResendLoading(false);
    }
  }, [authHeaders, selectedEventSlug, loadEventRegistrations, loadEventSummaries]);

  const handleBatchUndoCheckIn = useCallback(async () => {
    if (!selectedEventSlug) return;
    const confirmed = window.confirm(
      "Are you sure you want to undo check-in for ALL currently checked-in participants of this event?\n\n確定要取消此活動所有已入場登記參加者的入場登記嗎？",
    );
    if (!confirmed) return;
    setError("");
    setBatchUndoCheckInLoading(true);
    try {
      const res = await fetch(`/api/admin/events/${encodeURIComponent(selectedEventSlug)}/batch-undo-checkin`, {
        method: "POST",
        headers: authHeaders,
      });
      const data = (await res.json().catch(() => null)) as { ok?: boolean; error?: string; total?: number } | null;
      if (!res.ok || !data?.ok) throw new Error(data?.error || "batch_undo_checkin_failed");
      alert(`Undid check-in for ${data.total ?? 0} participant(s). / 已取消 ${data.total ?? 0} 位參加者的入場登記。`);
      await loadEventSummaries();
      await loadEventRegistrations();
    } catch {
      setError("Failed to undo check-in.");
    } finally {
      setBatchUndoCheckInLoading(false);
    }
  }, [authHeaders, selectedEventSlug, loadEventRegistrations, loadEventSummaries]);

  const sendCustomEmail = useCallback(async () => {
    const subject = customEmailSubject.trim();
    const body = customEmailBody;
    if (!subject || !body.trim() || selectedRegistrationIds.length === 0) return;

    const confirmed = window.confirm(
      `Send this email to ${selectedRegistrationIds.length} selected participant(s)?\n\n確定要寄出此電郵給已選取的 ${selectedRegistrationIds.length} 位參加者嗎？`,
    );
    if (!confirmed) return;

    setCustomEmailSending(true);
    setError("");
    try {
      const res = await fetch("/api/admin/event-registrations/custom-email", {
        method: "POST",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({
          registrationIds: selectedRegistrationIds,
          subject,
          body,
        }),
      });
      const data = (await res.json().catch(() => null)) as {
        ok?: boolean;
        error?: string;
        queued?: number;
        failed?: number;
      } | null;
      if (!res.ok || !data?.ok) throw new Error(data?.error || "send_failed");
      alert(`Queued: ${data.queued || 0}, Failed: ${data.failed || 0}`);
      setCustomEmailModalOpen(false);
      setCustomEmailSubject("");
      setCustomEmailBody("");
      setCustomEmailTab("edit");
      setSelectedRegistrationIds([]);
    } catch {
      setError("Failed to queue custom email.");
    } finally {
      setCustomEmailSending(false);
    }
  }, [authHeaders, customEmailBody, customEmailSubject, selectedRegistrationIds]);

  const sendCustomEmailTest = useCallback(async () => {
    const subject = customEmailSubject.trim();
    const body = customEmailBody;
    if (!subject || !body.trim()) return;

    const confirmed = window.confirm("Send a test email to yourself?\n\n確定要寄送測試電郵給自己嗎？");
    if (!confirmed) return;

    setCustomEmailSending(true);
    setError("");
    try {
      const res = await fetch("/api/admin/email/test-custom", {
        method: "POST",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({
          subject,
          body,
          eventSlug: selectedEventSlug || null,
        }),
      });
      const data = (await res.json().catch(() => null)) as {
        ok?: boolean;
        error?: string;
        recipientEmail?: string;
      } | null;
      if (!res.ok || !data?.ok) throw new Error(data?.error || "test_failed");
      alert(`Test email queued to: ${data.recipientEmail || "your email"}`);
    } catch {
      setError("Failed to queue test email.");
    } finally {
      setCustomEmailSending(false);
    }
  }, [authHeaders, customEmailBody, customEmailSubject, selectedEventSlug]);

  const handleReject = useCallback(
    async (registration: EventRegistrationRow) => {
      const confirmed = window.confirm(
        "No email will be sent to the user. Please handle communication manually.\n\n不會自動寄送電郵給用戶，請手動聯絡處理。",
      );
      if (!confirmed) return;
      const reason = window.prompt(
        "Reason for rejection",
        registration.statusReason || "Receipt could not be verified.",
      );
      if (reason === null) return;
      await updateEventRegistration(registration.id, "reject", { statusReason: reason });
    },
    [updateEventRegistration],
  );

  const handleDeleteParticipant = useCallback(
    async (registration: EventRegistrationRow) => {
      const step1 = window.confirm(
        `Delete registration for "${registration.fullName}" (${registration.email})?\n\n確定要刪除「${registration.fullName}」(${registration.email}) 的報名記錄？`,
      );
      if (!step1) return;
      const step2 = window.confirm(
        "This action CANNOT be undone. The receipt image and all registration data will be permanently deleted.\n\n此操作無法撤銷，收據圖片及所有報名資料將被永久刪除。",
      );
      if (!step2) return;

      setError("");
      try {
        const res = await fetch(`/api/admin/event-registrations/${encodeURIComponent(registration.id)}`, {
          method: "DELETE",
          headers: authHeaders,
        });
        const data = (await res.json().catch(() => null)) as { ok?: boolean; error?: string } | null;
        if (!res.ok || !data?.ok) throw new Error(data?.error || "delete_failed");
        await loadEventSummaries();
        await loadEventRegistrations();
      } catch {
        setError("Failed to delete registration. / 刪除報名記錄失敗。");
      }
    },
    [authHeaders, loadEventRegistrations, loadEventSummaries],
  );

  const handleSaveEdit = useCallback(
    async (id: string, updates: Partial<EventRegistrationRow>) => {
      setError("");
      setActionLoadingId(id);
      try {
        const res = await fetch(`/api/admin/event-registrations/${encodeURIComponent(id)}`, {
          method: "PUT",
          headers: { ...authHeaders, "Content-Type": "application/json" },
          body: JSON.stringify(updates),
        });
        const data = (await res.json().catch(() => null)) as { ok?: boolean; error?: string } | null;
        if (!res.ok || !data?.ok) throw new Error(data?.error || "update_failed");
        await loadEventSummaries();
        await loadEventRegistrations();
        setEditingRegistration(null);
      } catch {
        setError("Failed to update registration. / 更新報名記錄失敗。");
      } finally {
        setActionLoadingId("");
      }
    },
    [authHeaders, loadEventRegistrations, loadEventSummaries],
  );

  const handleExportExcel = useCallback(() => {
    if (eventRegistrations.length === 0) return;

    const rows = eventRegistrations.map((r) => {
      const baseRow: Record<string, string | number> = {
        Created: new Date(r.createdAt).toLocaleString(),
        Event: r.eventTitleZh,
        "Event Slug": r.eventSlug,
        "Full Name": r.fullName,
        "Display Name": r.displayName || "",
        Email: r.email,
        Phone: r.phone,
        "Ticket Type": r.ticketTypeNameZh,
        "Ticket Price (HKD)": r.ticketPriceHkd ?? "",
        "E-Ticket Code": r.eTicketCode || "",
        Status: r.status,
        "Status Reason": r.statusReason || "",
        "Admin Notes": r.adminNotes || "",
        "Discount Code": r.discountCode || "",
        "Payment Method": r.paymentMethod || "",
        "Checked In At": r.checkedInAt ? new Date(r.checkedInAt).toLocaleString() : "",
        "Ticket Email Sent At": r.ticketEmailSentAt ? new Date(r.ticketEmailSentAt).toLocaleString() : "",
      };

      if (selectedEventSlug) {
        selectedEventCustomFieldKeys.forEach((fieldKey) => {
          baseRow[fieldKey] = r.customValues?.[fieldKey] || "";
        });
      } else {
        baseRow["Custom Values"] = r.customValues
          ? Object.entries(r.customValues)
              .map(([k, v]) => `${k}: ${v}`)
              .join("; ")
          : "";
      }

      return baseRow;
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Registrations");

    const eventLabel = selectedEventSlug || "all-events";
    XLSX.writeFile(wb, `registrations-${eventLabel}.xlsx`);
  }, [eventRegistrations, selectedEventCustomFieldKeys, selectedEventSlug]);

  useEffect(() => {
    if (!authed || !token.trim()) return;
    void loadEventSummaries();
    void loadEventRegistrations();
  }, [authed, token, loadEventRegistrations, loadEventSummaries]);

  useEffect(() => {
    if (!authed || !token.trim()) return;
    const handle = window.setTimeout(() => {
      void loadEventRegistrations();
    }, 200);
    return () => window.clearTimeout(handle);
  }, [eventSearch, authed, token, loadEventRegistrations]);

  useEffect(() => {
    if (!authed || !token.trim() || activeTab !== "email-logs") return;
    void loadEmailLogs();
  }, [authed, token, activeTab, loadEmailLogs]);

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <Navbar />
      <section className="pt-28 pb-20 md:pt-36 md:pb-28">
        <div className="w-[90%] mx-auto px-6 md:px-10">
          <div className="bg-white border border-gray-100 rounded-3xl p-6 md:p-8">
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">Event Ops</h1>
                <p className="text-sm text-gray-500">
                  Approve/reject event registrations, issue e-tickets, and manage on-site check-in.
                </p>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => setActiveTab("registrations")}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                    activeTab === "registrations"
                      ? "bg-[#9B72FF] text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  Registrations
                </button>
                <button
                  onClick={() => setActiveTab("email-logs")}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                    activeTab === "email-logs"
                      ? "bg-[#9B72FF] text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  Email Logs
                </button>
              </div>
            </div>

            {!authed ? (
              <div className="max-w-md">
                {import.meta.env.VITE_GOOGLE_CLIENT_ID ? (
                  <div className="mb-3">
                    <div ref={googleButtonRef} className="min-h-[44px]" />
                  </div>
                ) : (
                  <p className="text-sm text-red-600">Missing VITE_GOOGLE_CLIENT_ID. Google login is not configured.</p>
                )}
                {error ? <p className="text-sm text-red-600 mt-3">{error}</p> : null}
              </div>
            ) : (
              <>
                {activeTab === "registrations" && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
                      {eventSummaries.map((event) => (
                        <button
                          key={event.slug}
                          onClick={() => setSelectedEventSlug(event.slug)}
                          className={`text-left rounded-3xl border p-5 transition-colors ${
                            selectedEventSlug === event.slug
                              ? "border-[#9B72FF] bg-[#F8F5FF]"
                              : "border-gray-100 bg-white hover:border-gray-200"
                          }`}
                        >
                          <p className="text-lg font-semibold text-gray-900">{event.titleZh}</p>
                          <p className="text-sm text-gray-500 mt-1">{event.dateLabel}</p>
                          <p className="text-sm text-gray-500 mb-4">{event.venue}</p>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div className="rounded-2xl bg-white/80 px-3 py-2">
                              <p className="text-gray-400">Pending</p>
                              <p className="font-semibold text-gray-900">{event.pendingReviewCount}</p>
                            </div>
                            <div className="rounded-2xl bg-white/80 px-3 py-2">
                              <p className="text-gray-400">Approved</p>
                              <p className="font-semibold text-gray-900">{event.approvedCount}</p>
                            </div>
                            <div className="rounded-2xl bg-white/80 px-3 py-2">
                              <p className="text-gray-400">Rejected</p>
                              <p className="font-semibold text-gray-900">{event.rejectedCount}</p>
                            </div>
                            <div className="rounded-2xl bg-white/80 px-3 py-2">
                              <p className="text-gray-400">Checked In</p>
                              <p className="font-semibold text-gray-900">{event.checkedInCount}</p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>

                    <div className="overflow-x-auto w-full">
                      <div className="min-w-[1400px]">
                        <div className="flex flex-col sm:flex-row flex-wrap gap-3 mb-5">
                          <select
                            value={selectedEventSlug}
                            onChange={(event) => setSelectedEventSlug(event.target.value)}
                            className="min-w-[160px] border border-gray-200 rounded-2xl px-4 py-3 text-sm text-gray-900 bg-white"
                          >
                            <option value="">All Events</option>
                            {eventSummaries.map((event) => (
                              <option key={event.slug} value={event.slug}>
                                {event.titleZh}
                              </option>
                            ))}
                          </select>

                          <select
                            value={eventStatusFilter}
                            onChange={(event) => setEventStatusFilter(event.target.value)}
                            className="min-w-[160px] border border-gray-200 rounded-2xl px-4 py-3 text-sm text-gray-900 bg-white"
                          >
                            <option value="">All Statuses</option>
                            <option value="pending_review">Pending Review</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                          </select>

                          <input
                            type="text"
                            value={eventSearch}
                            onChange={(event) => setEventSearch(event.target.value)}
                            placeholder="Search by name, email, phone or ticket code"
                            className="flex-1 min-w-[240px] border border-gray-200 rounded-2xl px-4 py-3 text-sm text-gray-900"
                          />

                          <button
                            onClick={() => void loadEventRegistrations()}
                            disabled={loading}
                            className={`px-4 py-3 rounded-full text-sm font-semibold text-white transition-colors whitespace-nowrap ${
                              loading ? "bg-gray-300 cursor-not-allowed" : "bg-gray-900 hover:bg-gray-700"
                            }`}
                          >
                            {loading ? "Loading..." : "Refresh"}
                          </button>

                          <button
                            onClick={() => setShowColumnPicker((prev) => !prev)}
                            className="px-4 py-3 rounded-full text-sm font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors whitespace-nowrap"
                          >
                            Columns
                          </button>

                          <button
                            onClick={handleExportExcel}
                            disabled={eventRegistrations.length === 0}
                            className={`px-4 py-3 rounded-full text-sm font-semibold transition-colors whitespace-nowrap ${
                              eventRegistrations.length === 0
                                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                : "bg-[#E6D9FF] text-[#5B3FA0] hover:bg-[#D8C3FF]"
                            }`}
                          >
                            Export Excel
                          </button>

                          <button
                            onClick={() => setCustomEmailModalOpen(true)}
                            disabled={selectedRegistrationIds.length === 0 || customEmailSending}
                            className={`px-4 py-3 rounded-full text-sm font-semibold transition-colors whitespace-nowrap ${
                              selectedRegistrationIds.length === 0 || customEmailSending
                                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                : "bg-[#9B72FF] text-white hover:bg-[#7C55CC]"
                            }`}
                          >
                            Email Selected ({selectedRegistrationIds.length})
                          </button>

                          <button
                            onClick={handleBatchResend}
                            disabled={!selectedEventSlug || batchResendLoading}
                            className={`px-4 py-3 rounded-full text-sm font-semibold transition-colors whitespace-nowrap ${
                              !selectedEventSlug || batchResendLoading
                                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                : "bg-emerald-500 text-white hover:bg-emerald-600"
                            }`}
                          >
                            {batchResendLoading ? "Resending..." : "Batch Resend Tickets"}
                          </button>

                          <button
                            onClick={() => void handleBatchUndoCheckIn()}
                            disabled={!selectedEventSlug || batchUndoCheckInLoading}
                            className={`px-4 py-3 rounded-full text-sm font-semibold transition-colors whitespace-nowrap ${
                              !selectedEventSlug || batchUndoCheckInLoading
                                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                : "bg-amber-500 text-white hover:bg-amber-600"
                            }`}
                          >
                            {batchUndoCheckInLoading ? "Undoing..." : "Batch Undo Check-in"}
                          </button>
                        </div>

                        {showColumnPicker ? (
                          <div className="mb-5 rounded-2xl border border-gray-200 bg-gray-50 p-4">
                            <div className="flex items-center justify-between gap-3 mb-3">
                              <p className="text-sm font-semibold text-gray-800">Show / Hide Columns</p>
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  onClick={() => setVisibleColumnKeys(allColumns.map((column) => column.key))}
                                  className="text-xs font-semibold text-[#7C55CC] hover:underline"
                                >
                                  Show all
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setVisibleColumnKeys(["attendee", "contact", "status", "actions"])}
                                  className="text-xs font-semibold text-gray-500 hover:underline"
                                >
                                  Reset simple
                                </button>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-3">
                              {allColumns.map((column) => (
                                <label
                                  key={column.key}
                                  className="inline-flex items-center gap-2 text-sm text-gray-700"
                                >
                                  <input
                                    type="checkbox"
                                    checked={visibleColumnKeys.includes(column.key)}
                                    onChange={() => toggleColumnVisibility(column.key)}
                                    className="w-4 h-4 rounded border-gray-300"
                                  />
                                  <span>{column.label}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        ) : null}

                        {error ? <p className="text-sm text-red-600 mb-4">{error}</p> : null}

                        <div className="border border-gray-100 rounded-2xl">
                          <table className="min-w-[1400px] w-full text-left text-sm">
                            <thead className="bg-gray-50 text-gray-600">
                              <tr>
                                <th className="px-4 py-3 font-semibold" style={{ width: 56, minWidth: 56 }}>
                                  <input
                                    ref={selectAllRef}
                                    type="checkbox"
                                    checked={allVisibleSelected}
                                    onChange={(e) => toggleSelectAllVisible(e.target.checked)}
                                    className="w-4 h-4 rounded border-gray-300"
                                  />
                                </th>
                                {activeColumns.map((column) => (
                                  <th
                                    key={column.key}
                                    className="px-4 py-3 font-semibold relative select-none"
                                    style={{
                                      width: colWidths[column.key] || 120,
                                      minWidth: colWidths[column.key] || 120,
                                    }}
                                  >
                                    {column.label}
                                    <div
                                      onMouseDown={(e) => handleResizeStart(column.key, e)}
                                      className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-[#9B72FF]/30 z-10"
                                      style={{ userSelect: "none" }}
                                    />
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {eventRegistrations.map((row) => (
                                <tr key={row.id} className="text-gray-800 align-top">
                                  <td className="px-4 py-3" style={{ width: 56 }}>
                                    <input
                                      type="checkbox"
                                      checked={selectedRegistrationIdSet.has(row.id)}
                                      onChange={() => toggleSelectedRegistration(row.id)}
                                      className="w-4 h-4 rounded border-gray-300"
                                    />
                                  </td>
                                  {activeColumns.map((column) => {
                                    if (column.isCustom) {
                                      const fieldKey = column.key.replace("custom:", "");
                                      return (
                                        <td
                                          key={column.key}
                                          className="px-4 py-3"
                                          style={{ width: colWidths[column.key] || 140 }}
                                        >
                                          <span className="text-xs text-gray-500">
                                            {row.customValues?.[fieldKey] || "-"}
                                          </span>
                                        </td>
                                      );
                                    }

                                    switch (column.baseKey) {
                                      case "created":
                                        return (
                                          <td
                                            key={column.key}
                                            className="px-4 py-3 whitespace-nowrap"
                                            style={{ width: colWidths.created }}
                                          >
                                            {new Date(row.createdAt).toLocaleString()}
                                          </td>
                                        );
                                      case "event":
                                        return (
                                          <td key={column.key} className="px-4 py-3" style={{ width: colWidths.event }}>
                                            <div className="font-semibold">{row.eventTitleZh}</div>
                                            <div className="text-xs text-gray-500">{row.eventSlug}</div>
                                          </td>
                                        );
                                      case "attendee":
                                        return (
                                          <td
                                            key={column.key}
                                            className="px-4 py-3"
                                            style={{ width: colWidths.attendee }}
                                          >
                                            <div className="font-semibold">{row.fullName}</div>
                                            <div className="text-xs text-gray-500">{row.displayName || "-"}</div>
                                          </td>
                                        );
                                      case "contact":
                                        return (
                                          <td
                                            key={column.key}
                                            className="px-4 py-3"
                                            style={{ width: colWidths.contact }}
                                          >
                                            <div>{row.email}</div>
                                            <div className="text-xs text-gray-500">{row.phone}</div>
                                          </td>
                                        );
                                      case "ticket":
                                        return (
                                          <td
                                            key={column.key}
                                            className="px-4 py-3"
                                            style={{ width: colWidths.ticket }}
                                          >
                                            <div className="font-semibold">{row.ticketTypeNameZh}</div>
                                            <div className="text-xs text-gray-500">
                                              {row.ticketPriceHkd ? `HKD ${row.ticketPriceHkd}` : "-"}
                                            </div>
                                          </td>
                                        );
                                      case "status":
                                        return (
                                          <td
                                            key={column.key}
                                            className="px-4 py-3"
                                            style={{ width: colWidths.status }}
                                          >
                                            <span
                                              className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${statusBadgeClass[row.status]}`}
                                            >
                                              {row.status}
                                            </span>
                                            {row.statusReason ? (
                                              <div className="text-xs text-gray-500 mt-2">{row.statusReason}</div>
                                            ) : null}
                                          </td>
                                        );
                                      case "discCode":
                                        return (
                                          <td
                                            key={column.key}
                                            className="px-4 py-3"
                                            style={{ width: colWidths.discCode }}
                                          >
                                            <span className="font-mono text-xs">{row.discountCode || "-"}</span>
                                          </td>
                                        );
                                      case "payment":
                                        return (
                                          <td
                                            key={column.key}
                                            className="px-4 py-3"
                                            style={{ width: colWidths.payment }}
                                          >
                                            <span className="text-xs">{row.paymentMethod || "-"}</span>
                                          </td>
                                        );
                                      case "custom":
                                        return (
                                          <td
                                            key={column.key}
                                            className="px-4 py-3"
                                            style={{ width: colWidths.custom }}
                                          >
                                            <span className="text-xs text-gray-500">
                                              {row.customValues
                                                ? Object.entries(row.customValues)
                                                    .map(([k, v]) => `${k}: ${v}`)
                                                    .join("; ")
                                                : "-"}
                                            </span>
                                          </td>
                                        );
                                      case "eTicket":
                                        return (
                                          <td
                                            key={column.key}
                                            className="px-4 py-3"
                                            style={{ width: colWidths.eTicket }}
                                          >
                                            <div className="font-semibold">{row.eTicketCode || "-"}</div>
                                            {row.ticketUrl ? (
                                              <div className="flex flex-col items-start gap-1 mt-1">
                                                <a
                                                  href={row.ticketUrl}
                                                  target="_blank"
                                                  rel="noreferrer"
                                                  className="text-xs text-[#7C55CC] hover:underline"
                                                >
                                                  Open ticket
                                                </a>
                                                <button
                                                  type="button"
                                                  onClick={() => void resendTicketEmail(row.id)}
                                                  disabled={actionLoadingId === row.id || row.status !== "approved"}
                                                  className={`text-xs font-semibold transition-colors ${
                                                    actionLoadingId === row.id || row.status !== "approved"
                                                      ? "text-gray-400 cursor-not-allowed"
                                                      : "text-[#7C55CC] hover:underline"
                                                  }`}
                                                >
                                                  {actionLoadingId === row.id ? "Resending..." : "Resend ticket"}
                                                </button>
                                              </div>
                                            ) : (
                                              <div className="text-xs text-gray-400">Not issued</div>
                                            )}
                                            <div className="text-xs mt-1">
                                              {row.ticketEmailSentAt ? (
                                                <span className="text-gray-400">Email sent</span>
                                              ) : (
                                                <span className="font-semibold text-red-600">Email pending</span>
                                              )}
                                            </div>
                                          </td>
                                        );
                                      case "checkin":
                                        return (
                                          <td
                                            key={column.key}
                                            className="px-4 py-3"
                                            style={{ width: colWidths.checkin }}
                                          >
                                            <div className="font-semibold">
                                              {row.checkedInAt
                                                ? new Date(row.checkedInAt).toLocaleString()
                                                : "Not checked in"}
                                            </div>
                                          </td>
                                        );
                                      case "receipt":
                                        return (
                                          <td
                                            key={column.key}
                                            className="px-4 py-3"
                                            style={{ width: colWidths.receipt }}
                                          >
                                            {row.receiptUrl ? (
                                              <button
                                                onClick={() => setReceiptModal(row)}
                                                className="inline-flex rounded-full bg-gray-100 text-gray-700 text-xs font-semibold px-3 py-1.5 hover:bg-gray-200 cursor-pointer"
                                              >
                                                View receipt
                                              </button>
                                            ) : (
                                              "-"
                                            )}
                                          </td>
                                        );
                                      case "remarks":
                                        return (
                                          <td
                                            key={column.key}
                                            className="px-4 py-3"
                                            style={{ width: colWidths.remarks }}
                                          >
                                            {editingNoteId === row.id ? (
                                              <div className="flex flex-col gap-2">
                                                <textarea
                                                  value={noteInputValue}
                                                  onChange={(e) => setNoteInputValue(e.target.value)}
                                                  onKeyDown={(e) => {
                                                    if (e.key === "Enter" && !e.shiftKey) {
                                                      e.preventDefault();
                                                      void updateAdminNotes(row.id, noteInputValue);
                                                    } else if (e.key === "Escape") {
                                                      setEditingNoteId(null);
                                                      setNoteInputValue("");
                                                    }
                                                  }}
                                                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#9B72FF] focus:border-transparent resize-y min-h-[60px]"
                                                  placeholder="Add remarks..."
                                                  autoFocus
                                                />
                                                <div className="flex gap-2">
                                                  <button
                                                    onClick={() => void updateAdminNotes(row.id, noteInputValue)}
                                                    disabled={actionLoadingId === row.id}
                                                    className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#9B72FF] text-white hover:bg-[#7C55CC] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                  >
                                                    Save
                                                  </button>
                                                  <button
                                                    onClick={() => {
                                                      setEditingNoteId(null);
                                                      setNoteInputValue("");
                                                    }}
                                                    disabled={actionLoadingId === row.id}
                                                    className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                  >
                                                    Cancel
                                                  </button>
                                                </div>
                                              </div>
                                            ) : (
                                              <div
                                                onClick={() => {
                                                  setEditingNoteId(row.id);
                                                  setNoteInputValue(row.adminNotes || "");
                                                }}
                                                className={`cursor-pointer ${
                                                  row.adminNotes
                                                    ? "text-gray-700 hover:text-gray-900"
                                                    : "text-gray-400 hover:text-gray-600"
                                                }`}
                                              >
                                                {row.adminNotes ? (
                                                  <div className="line-clamp-2 text-xs">{row.adminNotes}</div>
                                                ) : (
                                                  <div className="text-xs">Click to add...</div>
                                                )}
                                              </div>
                                            )}
                                          </td>
                                        );
                                      case "actions":
                                        return (
                                          <td
                                            key={column.key}
                                            className="px-4 py-3"
                                            style={{ width: colWidths.actions }}
                                          >
                                            <div className="flex flex-col gap-2 min-w-[130px]">
                                              <button
                                                onClick={() => setEditingRegistration(row)}
                                                disabled={actionLoadingId === row.id}
                                                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                                                  actionLoadingId === row.id
                                                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                                    : "bg-[#E6D9FF] text-[#5B3FA0] hover:bg-[#D8C3FF]"
                                                }`}
                                              >
                                                Edit
                                              </button>
                                              <button
                                                onClick={() => void updateEventRegistration(row.id, "approve")}
                                                disabled={actionLoadingId === row.id || row.status === "approved"}
                                                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                                                  actionLoadingId === row.id || row.status === "approved"
                                                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                                    : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                                                }`}
                                              >
                                                {row.status === "approved" ? "Approved" : "Approve"}
                                              </button>
                                              <button
                                                onClick={() => void handleReject(row)}
                                                disabled={actionLoadingId === row.id}
                                                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                                                  actionLoadingId === row.id
                                                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                                    : "bg-red-50 text-red-700 hover:bg-red-100"
                                                }`}
                                              >
                                                Reject
                                              </button>
                                              <button
                                                onClick={() =>
                                                  row.checkedInAt
                                                    ? setUndoCheckInModal(row)
                                                    : void updateEventRegistration(row.id, "check-in")
                                                }
                                                disabled={actionLoadingId === row.id || row.status !== "approved"}
                                                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                                                  actionLoadingId === row.id || row.status !== "approved"
                                                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                                    : "bg-[#E6D9FF] text-[#5B3FA0] hover:bg-[#D8C3FF]"
                                                }`}
                                              >
                                                {row.checkedInAt ? "Checked In" : "Check In"}
                                              </button>
                                              <button
                                                onClick={() => void handleDeleteParticipant(row)}
                                                disabled={actionLoadingId === row.id}
                                                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                                                  actionLoadingId === row.id
                                                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                                    : "bg-red-100 text-red-600 hover:bg-red-200"
                                                }`}
                                              >
                                                Delete
                                              </button>
                                            </div>
                                          </td>
                                        );
                                      default:
                                        return null;
                                    }
                                  })}
                                </tr>
                              ))}
                              {eventRegistrations.length === 0 && !loading ? (
                                <tr>
                                  <td className="px-4 py-6 text-gray-500" colSpan={Math.max(activeColumns.length, 1)}>
                                    No event registrations found.
                                  </td>
                                </tr>
                              ) : null}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {activeTab === "email-logs" && (
                  <>
                    <div className="flex flex-col lg:flex-row gap-3 mb-5">
                      <select
                        value={emailLogEventSlug}
                        onChange={(e) => setEmailLogEventSlug(e.target.value)}
                        className="border border-gray-200 rounded-2xl px-4 py-3 text-sm text-gray-900 bg-white"
                      >
                        <option value="">All Events</option>
                        {eventSummaries.map((event) => (
                          <option key={event.slug} value={event.slug}>
                            {event.titleZh}
                          </option>
                        ))}
                      </select>

                      <select
                        value={emailLogStatus}
                        onChange={(e) => setEmailLogStatus(e.target.value)}
                        className="border border-gray-200 rounded-2xl px-4 py-3 text-sm text-gray-900 bg-white"
                      >
                        <option value="">All Statuses</option>
                        <option value="success">Success</option>
                        <option value="failure">Failure</option>
                      </select>

                      <select
                        value={emailLogType}
                        onChange={(e) => setEmailLogType(e.target.value)}
                        className="border border-gray-200 rounded-2xl px-4 py-3 text-sm text-gray-900 bg-white"
                      >
                        <option value="">All Types</option>
                        <option value="pending_review">Pending Review</option>
                        <option value="approved_ticket">Approved Ticket</option>
                        <option value="resend_ticket">Resend Ticket</option>
                      </select>

                      <input
                        type="text"
                        value={emailLogRecipient}
                        onChange={(e) => setEmailLogRecipient(e.target.value)}
                        placeholder="Filter by recipient email"
                        className="flex-1 border border-gray-200 rounded-2xl px-4 py-3 text-sm text-gray-900"
                      />

                      <button
                        onClick={() => void loadEmailLogs()}
                        disabled={emailLogsLoading}
                        className={`px-4 py-2 rounded-full text-sm font-semibold text-white transition-colors whitespace-nowrap ${
                          emailLogsLoading ? "bg-gray-300 cursor-not-allowed" : "bg-gray-900 hover:bg-gray-700"
                        }`}
                      >
                        {emailLogsLoading ? "Loading..." : "Refresh"}
                      </button>
                    </div>

                    {error ? <p className="text-sm text-red-600 mb-4">{error}</p> : null}

                    <div className="overflow-auto border border-gray-100 rounded-2xl">
                      <table className="min-w-full w-full text-left text-sm">
                        <thead className="bg-gray-50 text-gray-600">
                          <tr>
                            <th className="px-4 py-3 font-semibold">Created</th>
                            <th className="px-4 py-3 font-semibold">Event</th>
                            <th className="px-4 py-3 font-semibold">Recipient</th>
                            <th className="px-4 py-3 font-semibold">Type</th>
                            <th className="px-4 py-3 font-semibold">Status</th>
                            <th className="px-4 py-3 font-semibold">Error</th>
                            <th className="px-4 py-3 font-semibold">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {emailLogs.map((log) => (
                            <tr key={log.id} className="text-gray-800 align-top">
                              <td className="px-4 py-3 whitespace-nowrap">
                                {new Date(log.createdAt).toLocaleString()}
                              </td>
                              <td className="px-4 py-3">
                                <span className="text-sm">{log.eventSlug || "-"}</span>
                              </td>
                              <td className="px-4 py-3">
                                <span className="text-sm">{log.recipientEmail}</span>
                              </td>
                              <td className="px-4 py-3">
                                <span className="text-sm capitalize">{log.emailType.replace(/_/g, " ")}</span>
                              </td>
                              <td className="px-4 py-3">
                                <span
                                  className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                                    log.status === "success"
                                      ? "bg-emerald-50 text-emerald-700"
                                      : "bg-red-50 text-red-700"
                                  }`}
                                >
                                  {log.status}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                {log.errorMessage ? (
                                  <span className="text-xs text-red-600">{log.errorMessage}</span>
                                ) : (
                                  "-"
                                )}
                              </td>
                              <td className="px-4 py-3">
                                <button
                                  onClick={() => setSelectedEmailLog(log)}
                                  className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#E6D9FF] text-[#5B3FA0] hover:bg-[#D8C3FF] transition-colors"
                                >
                                  View Details
                                </button>
                              </td>
                            </tr>
                          ))}
                          {emailLogs.length === 0 && !emailLogsLoading ? (
                            <tr>
                              <td className="px-4 py-6 text-gray-500" colSpan={7}>
                                No email logs found.
                              </td>
                            </tr>
                          ) : null}
                        </tbody>
                      </table>
                    </div>

                    {selectedEmailLog && (
                      <div
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
                        onClick={() => setSelectedEmailLog(null)}
                      >
                        <div
                          className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                            <div>
                              <p className="font-semibold text-gray-900">Email Log Details</p>
                              <p className="text-xs text-gray-500">
                                {new Date(selectedEmailLog.createdAt).toLocaleString()}
                              </p>
                            </div>
                            <button
                              onClick={() => setSelectedEmailLog(null)}
                              className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 text-lg leading-none cursor-pointer"
                            >
                              ×
                            </button>
                          </div>
                          <div className="flex-1 overflow-auto p-6 space-y-4">
                            <div>
                              <p className="text-xs text-gray-500 mb-1">Recipient Email</p>
                              <p className="text-sm text-gray-900">{selectedEmailLog.recipientEmail}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 mb-1">Email Type</p>
                              <p className="text-sm text-gray-900 capitalize">
                                {selectedEmailLog.emailType.replace(/_/g, " ")}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 mb-1">Status</p>
                              <span
                                className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                                  selectedEmailLog.status === "success"
                                    ? "bg-emerald-50 text-emerald-700"
                                    : "bg-red-50 text-red-700"
                                }`}
                              >
                                {selectedEmailLog.status}
                              </span>
                            </div>
                            {selectedEmailLog.errorMessage && (
                              <div>
                                <p className="text-xs text-gray-500 mb-1">Error Message</p>
                                <p className="text-sm text-red-600">{selectedEmailLog.errorMessage}</p>
                              </div>
                            )}
                            {selectedEmailLog.metadata && (
                              <div>
                                <p className="text-xs text-gray-500 mb-1">Metadata</p>
                                <pre className="bg-gray-50 p-3 rounded-xl text-xs text-gray-700 overflow-x-auto">
                                  {JSON.stringify(selectedEmailLog.metadata, null, 2)}
                                </pre>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {customEmailModalOpen ? (
                  <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
                    onClick={() => (customEmailSending ? null : setCustomEmailModalOpen(false))}
                  >
                    <div
                      className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                        <div>
                          <p className="font-semibold text-gray-900">Custom Email</p>
                          <p className="text-xs text-gray-500">Recipients: {selectedRegistrationIds.length}</p>
                        </div>
                        <button
                          onClick={() => (customEmailSending ? null : setCustomEmailModalOpen(false))}
                          className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 text-lg leading-none cursor-pointer"
                        >
                          &times;
                        </button>
                      </div>

                      <div className="flex-1 overflow-auto p-6 space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                          <input
                            type="text"
                            value={customEmailSubject}
                            onChange={(e) => setCustomEmailSubject(e.target.value)}
                            className="w-full border border-gray-200 rounded-2xl px-4 py-2 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-[#9B72FF] focus:border-transparent"
                            placeholder="Subject"
                          />
                        </div>

                        <div>
                          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={() => setCustomEmailTab("edit")}
                                className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                                  customEmailTab === "edit"
                                    ? "bg-[#9B72FF] text-white"
                                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                }`}
                              >
                                Compose
                              </button>
                              <button
                                type="button"
                                onClick={() => setCustomEmailTab("preview")}
                                className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                                  customEmailTab === "preview"
                                    ? "bg-[#9B72FF] text-white"
                                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                }`}
                              >
                                Preview
                              </button>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {[
                                "{{fullName}}",
                                "{{displayName}}",
                                "{{eventSlug}}",
                                "{{eTicketCode}}",
                                "{{ticketUrl}}",
                              ].map((token) => (
                                <button
                                  key={token}
                                  type="button"
                                  onClick={() => insertCustomEmailToken(token)}
                                  className="px-3 py-1.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                                >
                                  {token}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Body</label>
                          {customEmailTab === "edit" ? (
                            <div className="rounded-2xl border border-gray-200 overflow-hidden bg-white">
                              <Editor
                                licenseKey="gpl"
                                onInit={(_evt, editor) => {
                                  customEmailEditorRef.current = editor;
                                }}
                                value={customEmailBody}
                                onEditorChange={(value) => setCustomEmailBody(value)}
                                init={{
                                  height: 320,
                                  menubar: false,
                                  skin: false,
                                  content_css: false,
                                  plugins: ["autolink", "link", "lists", "table", "code"],
                                  toolbar:
                                    "undo redo | bold italic underline | alignleft aligncenter alignright | bullist numlist | link table | code",
                                  content_style:
                                    "body{font-family:Arial,sans-serif;font-size:14px;color:#111827} a{color:#7C55CC}",
                                }}
                              />
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <p className="text-xs text-gray-500">
                                {previewRegistration
                                  ? `Previewing as: ${previewRegistration.fullName} (${previewRegistration.email})`
                                  : "Select at least 1 recipient to preview placeholders with real user data."}
                              </p>
                              <div className="rounded-2xl border border-gray-200 overflow-hidden bg-white">
                                <iframe
                                  title="Email preview"
                                  sandbox=""
                                  className="w-full h-[320px]"
                                  srcDoc={`<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><style>body{margin:0;padding:16px;font-family:Arial,sans-serif;color:#111827;background:#ffffff}a{color:#7C55CC}</style></head><body>${customEmailBodyPreview}</body></html>`}
                                />
                              </div>
                            </div>
                          )}
                        </div>

                        {error ? <p className="text-sm text-red-600">{error}</p> : null}
                      </div>

                      <div className="border-t border-gray-100 px-6 py-4 flex gap-3 justify-end">
                        <button
                          onClick={() => (customEmailSending ? null : setCustomEmailModalOpen(false))}
                          disabled={customEmailSending}
                          className="px-5 py-2.5 rounded-full text-sm font-semibold bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => void sendCustomEmailTest()}
                          disabled={customEmailSending || !customEmailSubject.trim() || !customEmailBody.trim()}
                          className="px-5 py-2.5 rounded-full text-sm font-semibold bg-gray-900 text-white hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Send test to myself
                        </button>
                        <button
                          onClick={() => void sendCustomEmail()}
                          disabled={
                            customEmailSending ||
                            selectedRegistrationIds.length === 0 ||
                            !customEmailSubject.trim() ||
                            !customEmailBody.trim()
                          }
                          className="px-5 py-2.5 rounded-full text-sm font-semibold bg-[#9B72FF] text-white hover:bg-[#7C55CC] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {customEmailSending ? "Sending..." : "Send"}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null}

                {receiptModal ? (
                  <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
                    onClick={() => setReceiptModal(null)}
                  >
                    <div
                      className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                        <div>
                          <p className="font-semibold text-gray-900">{receiptModal.fullName}</p>
                          <p className="text-xs text-gray-500">{receiptModal.email}</p>
                        </div>
                        <button
                          onClick={() => setReceiptModal(null)}
                          className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 text-lg leading-none cursor-pointer"
                        >
                          &times;
                        </button>
                      </div>

                      <div className="flex-1 overflow-auto p-6">
                        <div className="rounded-2xl bg-gray-50 p-4 space-y-1.5 text-sm mb-4">
                          <div className="flex justify-between gap-3">
                            <span className="text-gray-500 shrink-0">Ticket / 門票</span>
                            <span className="font-semibold text-gray-900 text-right">
                              {receiptModal.ticketTypeNameZh}
                            </span>
                          </div>
                          <div className="flex justify-between gap-3">
                            <span className="text-gray-500 shrink-0">Payment / 付款方式</span>
                            <span className="font-semibold text-gray-900 text-right">
                              {receiptModal.paymentMethod || "-"}
                            </span>
                          </div>
                          {receiptModal.discountCode ? (
                            <div className="flex justify-between gap-3">
                              <span className="text-gray-500 shrink-0">Discount / 折扣</span>
                              <span className="font-semibold text-gray-900 text-right">
                                {receiptModal.discountCode}
                              </span>
                            </div>
                          ) : null}
                        </div>
                        {receiptModal.receiptUrl ? (
                          <img
                            src={receiptModal.receiptUrl}
                            alt={`Receipt for ${receiptModal.fullName}`}
                            className="w-full rounded-2xl border border-gray-100"
                          />
                        ) : (
                          <p className="text-gray-400 text-center py-8">No receipt image available.</p>
                        )}
                      </div>

                      <div className="border-t border-gray-100 px-6 py-4 flex gap-3 justify-end">
                        {receiptModal.status !== "approved" ? (
                          <button
                            onClick={() => {
                              updateEventRegistration(receiptModal.id, "approve");
                              setReceiptModal(null);
                            }}
                            disabled={actionLoadingId === receiptModal.id}
                            className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-colors ${
                              actionLoadingId === receiptModal.id
                                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                : "bg-emerald-500 text-white hover:bg-emerald-600 cursor-pointer"
                            }`}
                          >
                            {actionLoadingId === receiptModal.id ? "Processing..." : "Approve"}
                          </button>
                        ) : null}
                        <button
                          onClick={() => {
                            handleReject(receiptModal);
                            setReceiptModal(null);
                          }}
                          disabled={actionLoadingId === receiptModal.id}
                          className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-colors ${
                            actionLoadingId === receiptModal.id
                              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                              : "bg-red-500 text-white hover:bg-red-600 cursor-pointer"
                          }`}
                        >
                          {actionLoadingId === receiptModal.id ? "Processing..." : "Reject"}
                        </button>
                        {receiptModal.status === "approved" ? (
                          <button
                            disabled
                            className="px-5 py-2.5 rounded-full text-sm font-semibold bg-gray-100 text-gray-400 cursor-not-allowed"
                          >
                            Approved
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ) : null}

                {/* Undo Check-in Modal */}
                {undoCheckInModal ? (
                  <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
                    onClick={() => setUndoCheckInModal(null)}
                  >
                    <div
                      className="bg-white rounded-3xl max-w-md w-full overflow-hidden"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="px-6 pt-6 pb-5">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                            <svg
                              className="w-5 h-5 text-amber-600"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                              />
                            </svg>
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">Undo check-in?</p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {undoCheckInModal.fullName} · {undoCheckInModal.email}
                            </p>
                          </div>
                        </div>
                        <p className="mt-4 text-sm text-gray-600">
                          This attendee is currently checked in. Undo their check-in?
                          <br />
                          <span className="text-gray-400">此參加者已入場登記。確定要取消其入場登記嗎？</span>
                        </p>
                      </div>
                      <div className="border-t border-gray-100 px-6 py-4 flex gap-3 justify-end">
                        <button
                          onClick={() => setUndoCheckInModal(null)}
                          disabled={actionLoadingId === undoCheckInModal.id}
                          className="px-5 py-2.5 rounded-full text-sm font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 cursor-pointer"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => {
                            void updateEventRegistration(undoCheckInModal.id, "check-in");
                            setUndoCheckInModal(null);
                          }}
                          disabled={actionLoadingId === undoCheckInModal.id}
                          className="px-5 py-2.5 rounded-full text-sm font-semibold bg-amber-500 text-white hover:bg-amber-600 cursor-pointer"
                        >
                          {actionLoadingId === undoCheckInModal.id ? "Processing..." : "Undo Check-in"}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null}

                {/* Edit Registration Modal */}
                {editingRegistration && editFormData ? (
                  <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
                    onClick={() => setEditingRegistration(null)}
                  >
                    <div
                      className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                        <div>
                          <p className="font-semibold text-gray-900">Edit Registration</p>
                          <p className="text-xs text-gray-500">{editingRegistration.eventTitleZh}</p>
                        </div>
                        <button
                          onClick={() => setEditingRegistration(null)}
                          className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 text-lg leading-none cursor-pointer"
                        >
                          &times;
                        </button>
                      </div>

                      <div className="px-6 pt-4">
                        <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3">
                          <div className="flex items-start gap-2">
                            <svg
                              className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                              />
                            </svg>
                            <div className="text-sm text-amber-800">
                              <p className="font-semibold">No Validation Applied</p>
                              <p className="text-amber-700 mt-1">
                                Changes to registration data are not validated against registration rules. Please ensure
                                data is correct before saving.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex-1 overflow-auto p-6 space-y-4">
                        {/* Full Name */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                          <input
                            type="text"
                            value={editFormData.fullName}
                            onChange={(e) => setEditFormData({ ...editFormData, fullName: e.target.value })}
                            className="w-full border border-gray-200 rounded-2xl px-4 py-2 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-[#9B72FF] focus:border-transparent"
                          />
                        </div>

                        {/* Display Name */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
                          <input
                            type="text"
                            value={editFormData.displayName}
                            onChange={(e) => setEditFormData({ ...editFormData, displayName: e.target.value })}
                            className="w-full border border-gray-200 rounded-2xl px-4 py-2 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-[#9B72FF] focus:border-transparent"
                          />
                        </div>

                        {/* Phone */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                          <input
                            type="text"
                            value={editFormData.phone}
                            onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                            className="w-full border border-gray-200 rounded-2xl px-4 py-2 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-[#9B72FF] focus:border-transparent"
                          />
                        </div>

                        {/* Email */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                          <input
                            type="email"
                            value={editFormData.email}
                            onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                            className="w-full border border-gray-200 rounded-2xl px-4 py-2 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-[#9B72FF] focus:border-transparent"
                          />
                        </div>

                        {/* Ticket Type Code */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Ticket Type Code</label>
                          <input
                            type="text"
                            value={editFormData.ticketTypeCode}
                            onChange={(e) => setEditFormData({ ...editFormData, ticketTypeCode: e.target.value })}
                            className="w-full border border-gray-200 rounded-2xl px-4 py-2 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-[#9B72FF] focus:border-transparent"
                          />
                        </div>

                        {/* Discount Code */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Discount Code</label>
                          <input
                            type="text"
                            value={editFormData.discountCode}
                            onChange={(e) => setEditFormData({ ...editFormData, discountCode: e.target.value })}
                            className="w-full border border-gray-200 rounded-2xl px-4 py-2 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-[#9B72FF] focus:border-transparent"
                          />
                        </div>

                        {/* Custom Fields */}
                        {editEventCustomFields.length > 0 && editFormData && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Custom Fields</label>
                            <div className="space-y-3">
                              {editEventCustomFields.map((field) => (
                                <div key={field.id}>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">
                                    {field.labelZh || field.labelEn || field.fieldKey}
                                    {field.required && <span className="text-red-500 ml-1">*</span>}
                                  </label>
                                  {field.fieldType === "dropdown" && field.options.length > 0 ? (
                                    <select
                                      value={editFormData.customValues[field.fieldKey] || ""}
                                      onChange={(e) =>
                                        setEditFormData({
                                          ...editFormData,
                                          customValues: {
                                            ...editFormData.customValues,
                                            [field.fieldKey]: e.target.value,
                                          },
                                        })
                                      }
                                      className="w-full border border-gray-200 rounded-2xl px-4 py-2 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-[#9B72FF] focus:border-transparent"
                                    >
                                      <option value="">Select...</option>
                                      {field.options.map((option, idx) => (
                                        <option key={idx} value={option.en || option.zh || ""}>
                                          {option.zh || option.en}
                                        </option>
                                      ))}
                                    </select>
                                  ) : (
                                    <input
                                      type="text"
                                      value={editFormData.customValues[field.fieldKey] || ""}
                                      onChange={(e) =>
                                        setEditFormData({
                                          ...editFormData,
                                          customValues: {
                                            ...editFormData.customValues,
                                            [field.fieldKey]: e.target.value,
                                          },
                                        })
                                      }
                                      className="w-full border border-gray-200 rounded-2xl px-4 py-2 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-[#9B72FF] focus:border-transparent"
                                    />
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="border-t border-gray-100 px-6 py-4 flex gap-3 justify-end">
                        <button
                          onClick={() => setEditingRegistration(null)}
                          disabled={actionLoadingId === editingRegistration.id}
                          className="px-5 py-2.5 rounded-full text-sm font-semibold transition-colors bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => {
                            if (editFormData && editingRegistration) {
                              handleSaveEdit(editingRegistration.id, {
                                fullName: editFormData.fullName,
                                displayName: editFormData.displayName || null,
                                phone: editFormData.phone,
                                email: editFormData.email,
                                ticketTypeCode: editFormData.ticketTypeCode,
                                discountCode: editFormData.discountCode || null,
                                customValues: editFormData.customValues,
                              });
                            }
                          }}
                          disabled={actionLoadingId === editingRegistration.id}
                          className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-colors ${
                            actionLoadingId === editingRegistration.id
                              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                              : "bg-[#9B72FF] text-white hover:bg-[#7C55CC] cursor-pointer"
                          }`}
                        >
                          {actionLoadingId === editingRegistration.id ? "Saving..." : "Save"}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
